import { html, css, customElement, property } from "lit-element";
import { nothing } from "lit-html";
import { classMap } from "lit-html/directives/class-map";
import { BaseButton } from "@elements/_styles/buttons";
import "@elements/core/buttons/icon";
import "@elements/core/buttons/rectangle";

const STR_DEFAULT_CANCEL_TEXT = "Cancel";
const STR_DEFAULT_CONFIRM_TEXT = "Confirm";

@customElement("modal-confirm")
export class _ extends BaseButton {
    static get styles() {
        return [
            css`
                :host {
                    --overlay-z-index: 300;
                }

                .overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 100vw;
                    height: 100dvh;
                    opacity: 0.8;
                    background-color: var(--light-blue-3);
                    z-index: var(--overlay-z-index);
                    -webkit-backdrop-filter: blur(30px);
                    backdrop-filter: blur(30px);
                }
                .section {
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    z-index: var(--overlay-z-index);

                    width: 419px;
                    min-height: 276px;
                    border-radius: 16px;
                    box-shadow: 0 3px 16px 0 rgba(0, 0, 0, 0.16);
                    background-color: var(--white);
                    opacity: 1;
                    display: flex;
                    flex-direction: column;
                }

                .close {
                    align-self: flex-end;
                    margin: .5em .5em 0 0;
                }

                .contents {
                    display: flex;
                    flex-direction: column;
                    padding: 0 32px; 32px; 32px;
                    flex: 1;
                }
                .warning {
                    color: var(--dark-red-2);
                }

                .divider {
                  width: 354px;
                  height: 0;
                  border: solid 1px #d5e4ff;
                  margin-top: 16px;
                  margin-bottom: 24px;
                }
                .title {
                    font-size: 32px;
                    font-weight: bold;
                    font-stretch: normal;
                    font-style: normal;
                    line-height: 1.5;
                    letter-spacing: -0.32px;
                    text-align: left;
                }
                .content {
                  font-size: 16px;
                  font-weight: normal;
                  font-stretch: normal;
                  font-style: normal;
                  line-height: 1.5;
                  letter-spacing: normal;
                  text-align: left;
                  color: var(--dark-gray-6);
                  flex: 1;
                }
                .options {
                    margin: 2em 0 1em 0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                `,
        ];
    }

    onAnyClick(evt: MouseEvent) {
        const path = evt.composedPath();
        // Makes sure that only clicking the overlay will trigger a cancel event.
        if (!path.includes(this.shadowRoot?.getElementById("section") as any)) {
            this.onCancel();
        }
    }

    onCancel() {
        this.dispatchEvent(
            new CustomEvent("custom-cancel", {})
        );
    }

    onConfirm() {
        this.dispatchEvent(
            new CustomEvent("custom-confirm", {})
        );
    }

    @property({ type: String })
    title!: string;

    @property({ type: String })
    content!: string;

    @property({type: Boolean})
    render_cancel: boolean = true

    @property({ type: String })
    cancel_text: string = STR_DEFAULT_CANCEL_TEXT;

    @property({ type: String })
    cancelIcon?: string;

    @property({ type: String })
    confirm_text: string = STR_DEFAULT_CONFIRM_TEXT;

    @property({ type: String })
    confirmIcon?: string;

    @property({ type: Boolean })
    dangerous: boolean = false;

    buttonProps(isPrimary: Boolean) {
        let color = "blue";
        let kind = "filled";

        if (!isPrimary && this.dangerous) {
            color = "red";
            kind = "outline";
        } else if (isPrimary && this.dangerous) {
            color = "red"
        } else if (!isPrimary && !this.dangerous) {
            kind = "text";
        }

        return [color, kind];
    }

    renderConfirm() {
        const [color, kind] = this.buttonProps(true);

        const icon = this.confirmIcon
            ? html`<img-ui path="${this.confirmIcon}"></img-ui>`
            : nothing;

        return html`
            <div @click=${this.onConfirm}>
                <button-rect color=${color} kind=${kind}>
                    ${icon}
                    ${this.confirm_text}
                </button-rect>
            </div>
        `;
    }

    renderCancel() {
        const [color, kind] = this.buttonProps(false);

        const icon = this.cancelIcon
            ? html`<img-ui path="${this.cancelIcon}"></img-ui>`
            : nothing;

        return html`
            <div @click=${this.onCancel}>
                <button-rect color=${color} kind=${kind}>
                    ${icon}
                    ${this.cancel_text}
                </button-rect>
            </div>
        `;
    }

    renderActions() {
        return html`
            <div class="options">
                ${this.render_cancel ? this.renderCancel() : nothing}
                ${this.renderConfirm()}
            </div>
        `;
    }

    renderTitle() {
        const titleClasses = classMap({
            title: true,
            warning: this.dangerous,
        });


        return html`
            <div class="${titleClasses}">${this.title}</div>
        `;
    }

    render() {
        return html`
            <div class="overlay" @click=${this.onAnyClick}></div>
            <div class="section">
                <button-icon
                    size="x-small"
                    class="close"
                    icon="x"
                    @click=${this.onCancel}
                ></button-icon>
                <div class="contents">
                    ${this.renderTitle()}
                    <div class="divider"></div>
                    <div class="content">
                        <slot name="content">
                            ${this.content}
                        </slot>
                    </div>
                    ${this.renderActions()}
                </div>
            </div>
        `;
    }
}
